import { deriveIRLEvidenceSupportAssessment, SUPPORT_LEVELS } from './irlEvidenceSupportAssessment';
import { parseIrlValidationAnswer } from './irlEvidenceValidationParse';
import { htmlToPlainText } from './commonUtils';

/** User-facing Find Evidence verdict (3 states). */
export const FIND_EVIDENCE_VERDICT = {
    MATCHED: 'matched',
    PARTIALLY_MATCHED: 'partially_matched',
    NOT_MATCHED: 'not_matched',
};

export const FIND_EVIDENCE_LABELS = {
    [FIND_EVIDENCE_VERDICT.MATCHED]: 'Matched',
    [FIND_EVIDENCE_VERDICT.PARTIALLY_MATCHED]: 'Partially matched',
    [FIND_EVIDENCE_VERDICT.NOT_MATCHED]: 'Not matched',
};

export const FIND_EVIDENCE_SUMMARY = {
    [FIND_EVIDENCE_VERDICT.MATCHED]:
        'The management response aligns with what we found in the data room documents.',
    [FIND_EVIDENCE_VERDICT.PARTIALLY_MATCHED]:
        'Some of the management response is supported by VDR documents; other details need more proof or differ.',
    [FIND_EVIDENCE_VERDICT.NOT_MATCHED]:
        'The management response is not supported or is contradicted by data room evidence.',
};

const FIND_EVIDENCE_TONE = {
    [FIND_EVIDENCE_VERDICT.MATCHED]: 'success',
    [FIND_EVIDENCE_VERDICT.PARTIALLY_MATCHED]: 'warning',
    [FIND_EVIDENCE_VERDICT.NOT_MATCHED]: 'danger',
};

const API_VERDICT_MAP = {
    supported: FIND_EVIDENCE_VERDICT.MATCHED,
    partially_supported: FIND_EVIDENCE_VERDICT.PARTIALLY_MATCHED,
    contradicted: FIND_EVIDENCE_VERDICT.NOT_MATCHED,
    not_supported: FIND_EVIDENCE_VERDICT.NOT_MATCHED,
    no_vdr_evidence: FIND_EVIDENCE_VERDICT.NOT_MATCHED,
};

const LEVEL_MAP = {
    [SUPPORT_LEVELS.STRONGLY_SUPPORTED]: FIND_EVIDENCE_VERDICT.MATCHED,
    [SUPPORT_LEVELS.PARTIALLY_SUPPORTED]: FIND_EVIDENCE_VERDICT.PARTIALLY_MATCHED,
    [SUPPORT_LEVELS.WEAKLY_SUPPORTED]: FIND_EVIDENCE_VERDICT.PARTIALLY_MATCHED,
    [SUPPORT_LEVELS.CONTRADICTED]: FIND_EVIDENCE_VERDICT.NOT_MATCHED,
    [SUPPORT_LEVELS.NOT_SUPPORTED]: FIND_EVIDENCE_VERDICT.NOT_MATCHED,
    [SUPPORT_LEVELS.NOT_CORROBORATED]: FIND_EVIDENCE_VERDICT.NOT_MATCHED,
    [SUPPORT_LEVELS.DOES_NOT_MATCH]: FIND_EVIDENCE_VERDICT.NOT_MATCHED,
    [SUPPORT_LEVELS.NO_VDR_EVIDENCE]: FIND_EVIDENCE_VERDICT.NOT_MATCHED,
    [SUPPORT_LEVELS.INSUFFICIENT_EVIDENCE]: FIND_EVIDENCE_VERDICT.NOT_MATCHED,
};

/**
 * @param {object} response intelligence ask payload
 * @param {{ sourceIrlId?: number }} options
 */
export function resolveFindEvidenceVerdict(response, options = {}) {
    const assessment = deriveIRLEvidenceSupportAssessment(response, options);
    const apiVerdict = String(assessment?.irlValidation?.support_verdict || '').toLowerCase();

    let id = API_VERDICT_MAP[apiVerdict] || LEVEL_MAP[assessment?.level] || null;

    const vdrHits = Number(assessment?.retrievalHits?.vdr ?? response?.retrieval_debug?.vdr_hit_count ?? 0);
    if (!id && vdrHits === 0) {
        id = FIND_EVIDENCE_VERDICT.NOT_MATCHED;
    }
    if (!id) {
        id = FIND_EVIDENCE_VERDICT.PARTIALLY_MATCHED;
    }

    return {
        id,
        label: FIND_EVIDENCE_LABELS[id],
        summary: FIND_EVIDENCE_SUMMARY[id],
        tone: FIND_EVIDENCE_TONE[id],
        assessment,
        vdrDocumentCount: vdrHits,
    };
}

/**
 * Plain-text answer consultants can paste into the management response field.
 */
export function extractSuggestedManagementAnswer(response, irlRow) {
    const raw = String(response?.final_answer || response?.answer || '').trim();
    if (!raw) return '';

    const parsed = parseIrlValidationAnswer(raw);
    if (parsed.evidenceBasedAnswer && !/not answerable from retrieved vdr/i.test(parsed.evidenceBasedAnswer)) {
        return htmlToPlainText(parsed.evidenceBasedAnswer).trim();
    }

    const diligenceQuestion = htmlToPlainText(irlRow?.request || '').trim();
    const vdrPass = response?.findEvidence?.vdrPassSummary;
    if (vdrPass && !/not answerable/i.test(vdrPass)) {
        const vdrPlain = htmlToPlainText(vdrPass).trim();
        if (diligenceQuestion && vdrPlain.length > 40) {
            return vdrPlain;
        }
    }

    if (parsed.validationBody) {
        const verdictMatch = parsed.validationBody.match(
            /##\s*VDR Evidence Verdict\s*([\s\S]*?)(?=##|$)/i,
        );
        if (verdictMatch) {
            const section = htmlToPlainText(verdictMatch[1]).trim();
            if (section && !/^not supported/i.test(section)) {
                return section.slice(0, 4000);
            }
        }
    }

    return '';
}
